import React, { useEffect, useState, useRef } from "react";
import { collection, query, where, getDocs, orderBy, doc, getDoc } from "firebase/firestore";
import { db } from "../../firebase";
import { THEME_COLOR, CLUB_NAME, LEVELS, LEVELS_MAP } from "../../config/club";
import { toPng } from 'html-to-image';
import toast from "react-hot-toast";
import { useAuth } from "../../context/AuthContext";

export default function MatchHistoryModal({
    session,
    clubId,
    uid,
    onClose,
    isOwnProfile = false,
    passedStats = null, // New Prop
    passedRank = null   // New Prop
}) {
    const [stats, setStats] = useState(passedStats || { wins: 0, losses: 0 });
    const [rank, setRank] = useState(passedRank);
    const [matches, setMatches] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isTransparent, setIsTransparent] = useState(true);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [isGenerating, setIsGenerating] = useState(false);
    const [layoutMode, setLayoutMode] = useState('HERO');
    const [showShareOptions, setShowShareOptions] = useState(false);
    const [showFullPreview, setShowFullPreview] = useState(false);
    const [showFooter, setShowFooter] = useState(true);
    const { user } = useAuth();
    const shareRef = useRef(null);
    // Auto-sync the preview whenever the layout or style changes
    // Sync preview only when the panel is open
    useEffect(() => {
        if (showShareOptions) {
            const syncPreview = async () => {
                if (!shareRef.current) return;
                setIsGenerating(true);
                try {
                    const dataUrl = await toPng(shareRef.current, {
                        cacheBust: true,
                        pixelRatio: 2,
                        backgroundColor: isTransparent ? null : '#000',
                    });
                    setPreviewUrl(dataUrl);
                } catch (err) {
                    console.error("Preview sync failed", err);
                } finally {
                    setIsGenerating(false);
                }
            };
            syncPreview();
        }
    }, [layoutMode, isTransparent, matches, showShareOptions, showFooter]);
    const copyToClipboard = async () => {
        if (!previewUrl) return;
        try {
            const response = await fetch(previewUrl);
            const blob = await response.blob();

            // We use the ClipboardItem API
            const data = [new ClipboardItem({ [blob.type]: blob })];
            await navigator.clipboard.write(data);

            toast.success("Copied! Open Instagram and 'Paste' or 'Add Sticker' to put this over your photo.");
        } catch (err) {
            console.error("Copy failed", err);
            toast.error("Clipboard copy not supported on this browser. Use 'Share' or 'Save' instead.");
        }
    };
    const CLUB_LOGO_URL = "/img/rrc.png";
    //console.log(user)
    // 1. Fetch Data
    useEffect(() => {
        const fetchMatchDetails = async () => {
            try {
                // Fetch ONLY the match history list
                const q = query(
                    collection(db, "clubs", clubId, "matchHistory"),
                    where("sessionId", "==", session.id),
                    where("playerIds", "array-contains", uid),
                    orderBy("playedAt", "desc")
                );
                const matchSnap = await getDocs(q);
                const userMatches = matchSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                setMatches(userMatches);

                // If for some reason stats weren't passed, calculate them locally
                if (!passedStats) {
                    const wins = userMatches.filter(m => {
                        const [a, b] = m.score.split("-").map(Number);
                        const onTeamA = m.players.slice(0, 2).some(p => p.id === uid);
                        return onTeamA ? a > b : b > a;
                    }).length;
                    setStats({ wins, losses: userMatches.length - wins });
                }
            } catch (err) {
                console.error("Error fetching matches:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchMatchDetails();
    }, [session.id, clubId, uid, passedStats]);

    // 3. Share Logic
    const handleShare = async () => {
        if (!previewUrl) return;
        try {
            const blob = await (await fetch(previewUrl)).blob();
            const file = new File([blob], 'match-stats.png', { type: 'image/png' });

            if (navigator.canShare && navigator.canShare({ files: [file] })) {
                await navigator.share({ files: [file], title: 'My Results' });
            } else {
                const link = document.createElement('a');
                link.download = 'stats.png';
                link.href = previewUrl;
                link.click();
            }
        } catch (err) {
            console.error("Share failed", err);
        }
    };

    return (
        <>
            <div className="modal fade show d-block" style={{ backgroundColor: "rgba(0,0,0,0.8)", backdropFilter: "blur(8px)", zIndex: 1050 }}>
                <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable">
                    <div
                        className="modal-content border-0 rounded-5 shadow-lg"
                        style={{
                            backgroundColor: "var(--card-bg)",
                            color: "var(--text-color)"
                        }}
                    >
                        <div className="modal-header border-0 pb-0 pt-4 px-4 d-block">
                            <div className="d-flex justify-content-between align-items-start mb-3">
                                {/* Modern Date & Status Badge */}
                                <div className="d-flex align-items-center gap-2">
                                    <div
                                        className="px-3 py-1 rounded-pill"
                                        style={{
                                            background: `${THEME_COLOR}10`,
                                            backdropFilter: 'blur(10px)',
                                            border: `1px solid ${THEME_COLOR}20`,
                                        }}
                                    >
                                        <span style={{
                                            color: THEME_COLOR,
                                            fontSize: '0.65rem',
                                            fontWeight: '900',
                                            textTransform: 'uppercase',
                                            letterSpacing: '1.5px'
                                        }}>
                                            {session.endedAt?.toDate
                                                ? new Date(session.endedAt.toDate()).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                                                : 'Live Session'}
                                        </span>
                                    </div>

                                    {rank && (
                                        <div
                                            className="px-3 py-1 rounded-pill"
                                            style={{
                                                background: THEME_COLOR,
                                                border: '1px solid #334155'
                                            }}
                                        >
                                            <span style={{
                                                color: '#F8FAFC',
                                                fontSize: '0.65rem',
                                                fontWeight: '800',
                                                textTransform: 'uppercase',
                                                letterSpacing: '1px'
                                            }}>
                                                Rank #{rank}
                                            </span>
                                        </div>
                                    )}
                                </div>

                                {/* Minimalist Close Button */}
                                <button
                                    type="button"
                                    className="btn-close shadow-none opacity-50"
                                    onClick={onClose}
                                    style={{ fontSize: '0.8rem' }}
                                ></button>
                            </div>

                            {/* Typography Stack for Long Names */}
                            <div className="pe-4">
                                <h3 className="fw-black mb-1" style={{
                                    color: "var(--text-color)",
                                    fontSize: '1.75rem',
                                    lineHeight: '1.1',
                                    letterSpacing: '-0.04em',
                                    wordBreak: 'break-word'
                                }}>
                                    {session.name}
                                </h3>

                                <div className="d-flex align-items-center gap-2 mt-2">
                                    <div
                                        style={{
                                            width: '12px',
                                            height: '12px',
                                            borderRadius: '3px',
                                            backgroundColor: rank === 1 ? '#FFD700' : rank <= 3 ? '#94A3B8' : THEME_COLOR
                                        }}
                                    />
                                    <span className="text-muted fw-bold text-uppercase" style={{ fontSize: '0.7rem', letterSpacing: '2px' }}>
                                        {rank === 1 ? 'Top Seed' : rank <= 3 ? 'Elite Tier' : rank <= 10 ? 'High Flyer' : 'Contender'}
                                    </span>
                                </div>
                            </div>

                            {/* Subtle Decorative Line */}
                            <div className="mt-4" style={{
                                height: '1px',
                                width: '100%',
                                background: 'linear-gradient(90deg, #F1F5F9 0%, #E2E8F0 50%, #F1F5F9 100%)'
                            }} />
                        </div>

                        <div className="modal-body p-4">
                            {/* Session Stats Header */}
                            {!loading && matches.length > 0 && (
                                <div className="mb-4">

                                    <div
                                        className="d-flex align-items-stretch shadow-sm rounded-4 overflow-hidden"
                                        style={{
                                            border: "1px solid var(--border-color)",
                                            backgroundColor: "var(--card-bg)"
                                        }}
                                    >
                                        {/* Wins Section */}
                                        <div
                                            className="flex-grow-1 p-3 text-center"
                                            style={{ borderRight: "1px solid var(--border-color)" }}
                                        >
                                            <div
                                                className="text-uppercase fw-black text-success tracking-widest mb-1"
                                                style={{ fontSize: '0.65rem' }}
                                            >
                                                Wins
                                            </div>
                                            <div
                                                className="h3 mb-0 fw-black"
                                                style={{ color: "var(--text-color)" }}
                                            >
                                                {stats.wins}
                                            </div>
                                        </div>

                                        {/* Win Rate Section (Center) */}
                                        <div
                                            className="d-flex align-items-center justify-content-center px-3"
                                            style={{ backgroundColor: "var(--surface-bg)" }}
                                        >
                                            <div
                                                className="rounded-circle shadow-sm d-flex align-items-center justify-content-center fw-bold"
                                                style={{
                                                    width: '45px',
                                                    height: '45px',
                                                    fontSize: '0.75rem',
                                                    color: THEME_COLOR,
                                                    border: `2px solid ${THEME_COLOR}30`,
                                                    backgroundColor: "var(--card-bg)"
                                                }}
                                            >
                                                {Math.round((stats.wins / (stats.wins + stats.losses || 1)) * 100)}%
                                            </div>
                                        </div>

                                        {/* Losses Section */}
                                        <div
                                            className="flex-grow-1 p-3 text-center"
                                            style={{ borderLeft: "1px solid var(--border-color)" }}
                                        >
                                            <div
                                                className="text-uppercase fw-black text-danger tracking-widest mb-1"
                                                style={{ fontSize: '0.65rem' }}
                                            >
                                                Losses
                                            </div>
                                            <div
                                                className="h3 mb-0 fw-black"
                                                style={{ color: "var(--text-color)" }}
                                            >
                                                {stats.losses}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Sub-label */}
                                    <div className="text-center mt-2">
                                        <span
                                            className="fw-normal rounded-pill px-3 py-2"
                                            style={{
                                                fontSize: '0.7rem',
                                                backgroundColor: "var(--surface-bg)",
                                                color: "var(--muted-text)",
                                                border: "1px solid var(--border-color)"
                                            }}
                                        >
                                            <i className="bi bi-info-circle me-1"></i>
                                            Session performance based on {matches.length} matches
                                        </span>
                                    </div>

                                </div>
                            )}
                            {loading ? (
                                <div className="text-center py-5">
                                    <div className="spinner-border spinner-border-sm" style={{ color: THEME_COLOR }} />
                                </div>
                            ) : matches.length === 0 ? (
                                <div className="text-center py-4">
                                    <p className="text-muted small">No matches found for you in this session.</p>
                                </div>
                            ) : (
                                <div className="d-flex flex-column gap-3">
                                    {matches.map((m) => {
                                        const [a, b] = m.score.split("-").map(Number);
                                        const onTeamA = m.players.slice(0, 2).some(p => p.id === uid);
                                        const isWin = onTeamA ? a > b : b > a;

                                        return (
                                            <div
                                                key={m.id}
                                                className="p-3 rounded-4 shadow-sm mb-3"
                                                style={{
                                                    backgroundColor: "var(--card-bg)",
                                                    border: "1px solid var(--border-color)"
                                                }}
                                            >
                                                {/* Top Status Row */}
                                                <div className="d-flex justify-content-between align-items-center mb-3">
                                                    <span
                                                        className={`badge rounded-pill px-3 ${isWin ? "bg-success" : "bg-danger"}`}
                                                        style={{ fontSize: "0.6rem" }}
                                                    >
                                                        {isWin ? "WIN" : "LOSS"}
                                                    </span>

                                                    <span
                                                        className="fw-bold"
                                                        style={{
                                                            fontSize: "0.65rem",
                                                            color: "var(--muted-text)"
                                                        }}
                                                    >
                                                        {m.playedAt?.toDate().toLocaleTimeString([], {
                                                            hour: "2-digit",
                                                            minute: "2-digit",
                                                        })}
                                                    </span>
                                                </div>

                                                {/* Scoreboard Table */}
                                                <table className="w-100 text-center" style={{ tableLayout: "fixed" }}>
                                                    <tbody>
                                                        <tr>
                                                            <td
                                                                className={`text-truncate p-1 ${onTeamA && m.players[0].id === uid
                                                                        ? "fw-bold"
                                                                        : ""
                                                                    }`}
                                                                style={{
                                                                    fontSize: "0.85rem",
                                                                    color:
                                                                        onTeamA && m.players[0].id === uid
                                                                            ? THEME_COLOR
                                                                            : "var(--text-color)",
                                                                }}
                                                            >
                                                                {m.players[0]?.name}
                                                            </td>

                                                            <td rowSpan="3" className="align-middle px-2">
                                                                <div
                                                                    className="fw-black py-2 rounded-3"
                                                                    style={{
                                                                        fontSize: "1.4rem",
                                                                        color: THEME_COLOR,
                                                                        backgroundColor: "var(--surface-bg)",
                                                                    }}
                                                                >
                                                                    {m.score}
                                                                </div>
                                                            </td>

                                                            <td
                                                                className={`text-truncate p-1 ${!onTeamA && m.players[2].id === uid
                                                                        ? "fw-bold"
                                                                        : ""
                                                                    }`}
                                                                style={{
                                                                    fontSize: "0.85rem",
                                                                    color:
                                                                        !onTeamA && m.players[2].id === uid
                                                                            ? THEME_COLOR
                                                                            : "var(--text-color)",
                                                                }}
                                                            >
                                                                {m.players[2]?.name}
                                                            </td>
                                                        </tr>

                                                        <tr>
                                                            <td
                                                                className="small"
                                                                style={{ fontSize: "0.6rem", color: "var(--muted-text)" }}
                                                            >
                                                                &
                                                            </td>
                                                            <td
                                                                className="small"
                                                                style={{ fontSize: "0.6rem", color: "var(--muted-text)" }}
                                                            >
                                                                &
                                                            </td>
                                                        </tr>

                                                        <tr>
                                                            <td
                                                                className={`text-truncate p-1 ${onTeamA && m.players[1].id === uid
                                                                        ? "fw-bold"
                                                                        : ""
                                                                    }`}
                                                                style={{
                                                                    fontSize: "0.85rem",
                                                                    color:
                                                                        onTeamA && m.players[1].id === uid
                                                                            ? THEME_COLOR
                                                                            : "var(--text-color)",
                                                                }}
                                                            >
                                                                {m.players[1]?.name}
                                                            </td>

                                                            <td
                                                                className={`text-truncate p-1 ${!onTeamA && m.players[3].id === uid
                                                                        ? "fw-bold"
                                                                        : ""
                                                                    }`}
                                                                style={{
                                                                    fontSize: "0.85rem",
                                                                    color:
                                                                        !onTeamA && m.players[3].id === uid
                                                                            ? THEME_COLOR
                                                                            : "var(--text-color)",
                                                                }}
                                                            >
                                                                {m.players[3]?.name}
                                                            </td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        {/* --- COLLAPSIBLE SHARING PANEL --- */}
                        {isOwnProfile && (
                            <div
                                style={{
                                    position: "relative",
                                    zIndex: 1100,
                                    backgroundColor: "var(--card-bg)",
                                    borderTop: "1px solid var(--border-color)",
                                    boxShadow: "0 -4px 12px rgba(0,0,0,0.15)"
                                }}
                            >
                                {/* Pull Tab / Toggle Button */}
                                <button
                                    onClick={() => setShowShareOptions(!showShareOptions)}
                                    className="btn w-100 rounded-0 border-0 py-2 d-flex align-items-center justify-content-center gap-2"
                                    style={{
                                        backgroundColor: "var(--surface-bg)",
                                        color: "var(--text-color)"
                                    }}
                                >
                                    <i className={`bi bi-chevron-${showShareOptions ? "down" : "up"}`}></i>
                                    <span className="fw-bold small text-uppercase">
                                        {showShareOptions ? "Hide Sharing Options" : "Share My Results"}
                                    </span>
                                </button>

                                <div
                                    className="overflow-hidden"
                                    style={{
                                        maxHeight: showShareOptions ? "600px" : "0px",
                                        transition: "max-height 0.4s ease-in-out",
                                        backgroundColor: "var(--surface-bg)"
                                    }}
                                >
                                    <div className="p-4 pt-2">

                                        {/* Layout Selector */}
                                        <div className="mb-4">
                                            <label
                                                className="small fw-bold text-uppercase mb-2 d-block"
                                                style={{
                                                    fontSize: "0.65rem",
                                                    color: "var(--muted-text)"
                                                }}
                                            >
                                                Card Layout
                                            </label>

                                            <div
                                                className="d-flex p-1 rounded-pill"
                                                style={{
                                                    backgroundColor: "var(--card-bg)",
                                                    border: "1px solid var(--border-color)"
                                                }}
                                            >
                                                {["HERO", "COMPACT", "LIST", "VIBE"].map((m) => (
                                                    <button
                                                        key={m}
                                                        onClick={() => setLayoutMode(m)}
                                                        className="flex-fill btn btn-sm border-0 rounded-pill py-2"
                                                        style={{
                                                            backgroundColor:
                                                                layoutMode === m ? THEME_COLOR : "transparent",
                                                            color:
                                                                layoutMode === m
                                                                    ? "#fff"
                                                                    : "var(--muted-text)",
                                                            transition: "0.2s"
                                                        }}
                                                    >
                                                        {m}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>

                                        <div
                                            className="d-flex align-items-center justify-content-between p-3 rounded-4"
                                            style={{
                                                backgroundColor: "var(--card-bg)",
                                                border: "1px solid var(--border-color)",
                                                gap: "20px"
                                            }}
                                        >
                                            {/* LEFT SIDE: Transparent Mode */}
                                            <div className="d-flex align-items-center justify-content-between flex-grow-1">
                                                <div style={{ marginRight: '10px' }}>
                                                    <div className="fw-bold small" style={{ color: "var(--text-color)", lineHeight: 1.2 }}>
                                                        Transparent
                                                    </div>
                                                    <div style={{ fontSize: "0.6rem", color: "var(--muted-text)" }}>
                                                        Sticker Mode
                                                    </div>
                                                </div>
                                                <div
                                                    onClick={() => setIsTransparent(!isTransparent)}
                                                    style={{
                                                        width: "40px",
                                                        height: "22px",
                                                        backgroundColor: isTransparent ? THEME_COLOR : "var(--border-color)",
                                                        borderRadius: "50px",
                                                        cursor: "pointer",
                                                        transition: "0.3s",
                                                        position: "relative",
                                                        flexShrink: 0
                                                    }}
                                                >
                                                    <div style={{
                                                        position: "absolute",
                                                        top: "2px",
                                                        left: isTransparent ? "20px" : "2px",
                                                        width: "18px",
                                                        height: "18px",
                                                        backgroundColor: "var(--card-bg)",
                                                        borderRadius: "50%",
                                                        transition: "0.3s"
                                                    }} />
                                                </div>
                                            </div>

                                            {/* MIDDLE DIVIDER */}
                                            <div style={{ width: '1px', height: '30px', backgroundColor: 'var(--border-color)' }} />

                                            {/* RIGHT SIDE: Footer Toggle */}
                                            <div className="d-flex align-items-center justify-content-between flex-grow-1">
                                                <div style={{ marginRight: '10px' }}>
                                                    <div className="fw-bold small" style={{ color: "var(--text-color)", lineHeight: 1.2 }}>
                                                        Club Badge
                                                    </div>
                                                    <div style={{ fontSize: "0.6rem", color: "var(--muted-text)" }}>
                                                        Show Footer
                                                    </div>
                                                </div>
                                                <div
                                                    onClick={() => setShowFooter(!showFooter)}
                                                    style={{
                                                        width: "40px",
                                                        height: "22px",
                                                        backgroundColor: showFooter ? THEME_COLOR : "var(--border-color)",
                                                        borderRadius: "50px",
                                                        cursor: "pointer",
                                                        transition: "0.3s",
                                                        position: "relative",
                                                        flexShrink: 0
                                                    }}
                                                >
                                                    <div style={{
                                                        position: "absolute",
                                                        top: "2px",
                                                        left: showFooter ? "20px" : "2px",
                                                        width: "18px",
                                                        height: "18px",
                                                        backgroundColor: "var(--card-bg)",
                                                        borderRadius: "50%",
                                                        transition: "0.3s"
                                                    }} />
                                                </div>
                                            </div>
                                        </div>

                                        {/* Preview */}
                                        <div className="text-center mb-3 position-relative mt-4">
                                            {isGenerating && (
                                                <div
                                                    className="position-absolute top-50 start-50 translate-middle spinner-border"
                                                    style={{ color: THEME_COLOR, zIndex: 1 }}
                                                />
                                            )}

                                            <img
                                                src={previewUrl}
                                                onClick={() => !isGenerating && setShowFullPreview(true)}
                                                className="img-fluid rounded-4 shadow-sm"
                                                style={{
                                                    maxHeight: "200px",
                                                    cursor: isGenerating ? "default" : "zoom-in",
                                                    opacity: isGenerating ? 0.5 : 1,
                                                    transition: "opacity 0.2s",
                                                    border: "1px solid var(--border-color)"
                                                }}
                                                alt="Profile Preview"
                                            />

                                            {!isGenerating && previewUrl && (
                                                <div
                                                    className="small mt-1"
                                                    style={{ color: "var(--muted-text)" }}
                                                >
                                                    <i className="bi bi-search me-1"></i>
                                                    Tap to enlarge
                                                </div>
                                            )}
                                        </div>

                                        {/* Actions */}
                                        <div className="d-grid gap-2">
                                            <button
                                                className="btn rounded-pill fw-bold py-2"
                                                style={{
                                                    backgroundColor: THEME_COLOR,
                                                    color: "#fff"
                                                }}
                                                onClick={handleShare}
                                            >
                                                <i className="bi bi-send-fill me-2"></i>
                                                Share / Download
                                            </button>

                                            {isTransparent && (
                                                <button
                                                    className="btn rounded-pill fw-bold py-2"
                                                    style={{
                                                        backgroundColor: "transparent",
                                                        border: `1px solid ${THEME_COLOR}`,
                                                        color: THEME_COLOR
                                                    }}
                                                    onClick={copyToClipboard}
                                                >
                                                    <i className="bi bi-clipboard-plus me-2"></i>
                                                    Copy as Sticker
                                                </button>
                                            )}
                                        </div>

                                    </div>
                                </div>
                            </div>
                        )}

                    </div>
                </div>
            </div>

            {/* --- PREMIUM HIDDEN CANVAS ---  */}
            <div style={{ position: 'absolute', left: '-9999px', top: '0' }}>
                <div ref={shareRef} style={{
                    width: '1080px', height: '1920px',
                    backgroundColor: isTransparent ? 'transparent' : '#050505', // Deep rich black
                    padding: '120px 80px', display: 'flex', flexDirection: 'column',
                    justifyContent: 'space-between', alignItems: 'center',
                    fontFamily: "'Inter', 'Helvetica Neue', sans-serif"
                }}>

                    {/* Choice 1: THE HERO (Refined & Dramatic) */}
                    {layoutMode === 'HERO' && (
                        <div style={{
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center',
                            width: '100%',
                            position: 'relative',
                            padding: '60px 40px',
                            borderRadius: '32px',
                            // --- STRAVA STICKER OVERLAY PROTECTION ---
                            background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.88) 0%, rgba(30, 41, 59, 0.75) 100%)',
                            backdropFilter: 'blur(25px) saturate(190%)',
                            WebkitBackdropFilter: 'blur(25px) saturate(190%)',
                            border: '1px solid rgba(255, 255, 255, 0.12)',
                            boxShadow: '0 30px 60px rgba(0, 0, 0, 0.45), inset 0 1px 0 rgba(255,255,255,0.1)',
                            overflow: 'hidden'
                        }}>

                          

                            {/* CLUB LOGO */}
                            <img src={CLUB_LOGO_URL} style={{
                                width: '180px',
                                height: 'auto',
                                marginBottom: '40px',
                                filter: 'drop-shadow(0 8px 16px rgba(0,0,0,0.6))',
                                zIndex: 1
                            }} alt="Club Logo" />

                            {/* HERO STATS DISPLAY BLOCK */}
                            <div style={{ position: 'relative', marginBottom: '30px', zIndex: 1 }}>
                                <div style={{
                                    fontSize: '16rem',
                                    fontWeight: '900',
                                    color: '#fff',
                                    lineHeight: '0.8',
                                    letterSpacing: '-6px',
                                    fontStyle: 'italic',
                                    textShadow: '0 15px 30px rgba(0,0,0,0.5)',
                                    WebkitTextStroke: '2px rgba(0,0,0,0.1)'
                                }}>
                                    {Math.round((stats.wins / (stats.wins + stats.losses || 1)) * 100)}%
                                </div>

                                {rank && (
                                    <div style={{
                                        position: 'absolute',
                                        top: '-35px',
                                        left: '-40px',
                                        backgroundColor: '#fff',
                                        padding: '8px 24px',
                                        borderRadius: '12px',
                                        color: '#0f172a',
                                        fontSize: '2.2rem',
                                        fontWeight: '900',
                                        boxShadow: '0 10px 25px rgba(0,0,0,0.4)',
                                        letterSpacing: '-1px'
                                    }}>
                                        #{rank}
                                    </div>
                                )}

                                <div style={{
                                    position: 'absolute',
                                    top: '-35px',
                                    right: '-40px',
                                    backgroundColor: THEME_COLOR,
                                    padding: '8px 24px',
                                    borderRadius: '12px',
                                    color: '#000',
                                    fontSize: '2.2rem',
                                    fontWeight: '900',
                                    boxShadow: '0 10px 25px rgba(0,0,0,0.4)'
                                }}>
                                    WR
                                </div>
                            </div>

                            {/* SESSION TITLE */}
                            <div style={{
                                fontSize: '1.6rem',
                                color: '#fff',
                                fontWeight: '800',
                                letterSpacing: '8px',
                                textTransform: 'uppercase',
                                marginBottom: '50px',
                                opacity: 0.9,
                                textShadow: '0 4px 10px rgba(0,0,0,0.5)',
                                textAlign: 'center',
                                zIndex: 1
                            }}>
                                {session.name}
                            </div>

                            {/* TALLY COUNTER SUMMARY SPLIT */}
                            <div style={{
                                display: 'flex',
                                gap: '80px',
                                background: 'rgba(255, 255, 255, 0.04)',
                                padding: '30px 80px',
                                borderRadius: '24px',
                                border: '1px solid rgba(255, 255, 255, 0.08)',
                                boxShadow: '0 15px 35px rgba(0,0,0,0.2)',
                                zIndex: 1
                            }}>
                                <div style={{ textAlign: 'center', minWidth: '100px' }}>
                                    <div style={{ fontSize: '1.2rem', color: THEME_COLOR, fontWeight: '900', letterSpacing: '2px' }}>WINS</div>
                                    <div style={{ fontSize: '5rem', color: '#fff', fontWeight: '900', lineHeight: '1.1' }}>{stats.wins}</div>
                                </div>

                                <div style={{ width: '1px', background: 'rgba(255,255,255,0.15)', margin: '10px 0' }} />

                                <div style={{ textAlign: 'center', minWidth: '100px' }}>
                                    <div style={{ fontSize: '1.2rem', color: '#f87171', fontWeight: '900', letterSpacing: '2px' }}>LOSSES</div>
                                    <div style={{ fontSize: '5rem', color: '#fff', fontWeight: '900', lineHeight: '1.1' }}>{stats.losses}</div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Choice 2: THE COMPACT PILL (Sleek Watermark) */}
                    {layoutMode === 'COMPACT' && (
                        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
                            <div style={{
                                // 1. WEBKIT PREFIX IS MANDATORY FOR IOS
                                WebkitBackdropFilter: 'blur(40px)',
                                backdropFilter: 'blur(40px)',

                                // 2. PREVENT PIXEL BLEEDING
                                isolation: 'isolate',
                                WebkitMaskImage: '-webkit-radial-gradient(white, black)', // Fixes border-radius overflow on iOS

                                // 3. BACKGROUND ADJUSTMENT
                                // Using hex with alpha or rgba is fine, but keep it consistent
                                background: isTransparent ? 'rgba(0, 0, 0, 0.4)' : 'rgba(15, 15, 15, 0.85)',

                                border: '1px solid rgba(255,255,255,0.15)',
                                padding: '40px 60px',
                                borderRadius: '120px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '40px',
                                boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)',

                                // 4. FORCE HARDWARE ACCELERATION
                                transform: 'translateZ(0)',
                            }}>
                                <img src={CLUB_LOGO_URL} style={{ width: '110px' }} />
                                <div style={{ height: '70px', width: '1px', background: 'rgba(255,255,255,0.2)' }} />
                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                    <span style={{ color: '#aaa', fontSize: '1.5rem', fontWeight: 'bold', letterSpacing: '2px' }}>RECORD</span>
                                    <span style={{ color: '#fff', fontSize: '3.5rem', fontWeight: '900' }}>{stats.wins}W - {stats.losses}L</span>
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                    <span style={{ color: '#aaa', fontSize: '1.2rem', fontWeight: 'bold', letterSpacing: '2px' }}>RANK</span>
                                    <span style={{ color: THEME_COLOR, fontSize: '3rem', fontWeight: '900' }}>#{rank || '--'}</span>
                                </div>
                                <div style={{ height: '70px', width: '1px', background: 'rgba(255,255,255,0.2)' }} />
                                <div style={{
                                    background: THEME_COLOR, width: '140px', height: '140px', borderRadius: '50%',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    color: '#ffffff', fontSize: '3rem', fontWeight: '900', marginLeft: '20px'
                                }}>
                                    {Math.round((stats.wins / (stats.wins + stats.losses || 1)) * 100)}%
                                </div>
                            </div>
                        </div>
                    )}

                    {/* CHOICE 3: THE GRIND (Match History - Ultra-Minimal Elite) */}
                    {layoutMode === 'LIST' && (
                        <div style={{
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            width: '940px',
                            position: 'relative',
                            // --- STRAVA STICKER OVERLAY PROTECTION ---
                            padding: '30px',
                            borderRadius: '24px',
                            background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.85) 0%, rgba(30, 41, 59, 0.7) 100%)',
                            backdropFilter: 'blur(20px) saturate(180%)',
                            WebkitBackdropFilter: 'blur(20px) saturate(180%)',
                            border: '1px solid rgba(255, 255, 255, 0.12)',
                            boxShadow: '0 20px 50px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255,255,255,0.1)',
                        }}>


                            {/* HEADER: Clean, High-Contrast Performance Layout */}
                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: '30px',
                                padding: '0 10px'
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                                    {CLUB_LOGO_URL ? (
                                        <img src={CLUB_LOGO_URL} style={{ height: '55px', width: '55px', objectFit: 'contain', filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.5))' }} alt="logo" />
                                    ) : (
                                        /* Fallback Pickleball Icon graphic if no logo */
                                        <div style={{ width: '50px', height: '50px', borderRadius: '50%', border: `3px dashed ${THEME_COLOR}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '900', color: THEME_COLOR, fontSize: '1.2rem' }}>P</div>
                                    )}
                                    <div style={{ width: '2px', height: '40px', background: 'rgba(255,255,255,0.15)' }} />
                                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                                        <span style={{ color: THEME_COLOR, fontSize: '0.85rem', fontWeight: '900', letterSpacing: '4px', textShadow: '0 1px 4px rgba(0,0,0,0.5)' }}>PICKLEBALL REC</span>
                                        <span style={{ color: '#fff', fontSize: '2.2rem', fontWeight: '800', letterSpacing: '-0.5px', textShadow: '0 2px 12px rgba(0,0,0,0.6)' }}>{session.name}</span>
                                    </div>
                                </div>

                                <div style={{ textAlign: 'right', display: 'flex', alignItems: 'center', gap: '25px' }}>
                                    <div>
                                        <div style={{ color: '#fff', fontSize: '3.2rem', fontWeight: '900', letterSpacing: '-1px', lineHeight: '1', textShadow: '0 2px 10px rgba(0,0,0,0.5)' }}>
                                            {stats.wins}<span style={{ color: THEME_COLOR, marginLeft: '2px' }}>W</span> <span style={{ color: 'rgba(255,255,255,0.3)', fontWeight: '300' }}>/</span> {stats.losses}<span style={{ color: '#f87171', marginLeft: '2px' }}>L</span>
                                        </div>
                                        <div style={{ color: THEME_COLOR, fontSize: '0.8rem', fontWeight: '900', letterSpacing: '2px', marginTop: '4px', textShadow: '0 1px 4px rgba(0,0,0,0.4)' }}>
                                            WIN RATE: {Math.round((stats.wins / (stats.wins + stats.losses || 1)) * 100)}%
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* MATCH LIST: Premium frosted panels */}
                            <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '6px',
                                borderRadius: '16px',
                                overflow: 'hidden'
                            }}>
                                {matches.slice(0, 6).map((m, idx) => {
                                    const [a, b] = m.score.split("-").map(Number);
                                    const userOnA = m.players.slice(0, 2).some(p => p.id === uid);

                                    const teamAplayers = userOnA ? m.players.slice(0, 2) : m.players.slice(2, 4);
                                    const teamBplayers = userOnA ? m.players.slice(2, 4) : m.players.slice(0, 2);

                                    const userScore = userOnA ? a : b;
                                    const oppScore = userOnA ? b : a;
                                    const isWin = userScore > oppScore;

                                    return (
                                        <div key={idx} style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            padding: '16px 24px',
                                            background: 'rgba(255, 255, 255, 0.03)',
                                            border: '1px solid rgba(255, 255, 255, 0.05)',
                                            borderRadius: '12px',
                                            boxShadow: '0 4px 15px rgba(0,0,0,0.15)'
                                        }}>
                                            {/* Status Bar Indicator */}
                                            <div style={{
                                                width: '6px',
                                                height: '38px',
                                                background: isWin ? THEME_COLOR : 'rgba(255,255,255,0.15)',
                                                marginRight: '24px',
                                                borderRadius: '3px',
                                                boxShadow: isWin ? `0 0 14px ${THEME_COLOR}aa` : 'none'
                                            }} />

                                            {/* Players Side */}
                                            <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '20px' }}>
                                                {/* Your Team */}
                                                <div style={{ display: 'flex', flexDirection: 'column', minWidth: '190px' }}>
                                                    <span style={{ color: '#fff', fontSize: '1.1rem', fontWeight: '800', letterSpacing: '-0.3px' }}>{teamAplayers[0]?.name || "Player 1"}</span>
                                                    <span style={{ color: 'rgba(255, 255, 255, 0.75)', fontSize: '1.05rem', fontWeight: '600' }}>{teamAplayers[1]?.name || "Player 2"}</span>
                                                </div>

                                                <span style={{ color: THEME_COLOR, fontWeight: '900', fontSize: '0.75rem', opacity: 0.4, padding: '3px 6px', borderRadius: '4px' }}>VS</span>

                                                {/* Opponents */}
                                                <div style={{ display: 'flex', flexDirection: 'column', minWidth: '190px' }}>
                                                    <span style={{ color: 'rgba(255, 255, 255, 0.65)', fontSize: '1rem', fontWeight: '500' }}>{teamBplayers[0]?.name || "Opponent 1"}</span>
                                                    <span style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '1rem', fontWeight: '400' }}>{teamBplayers[1]?.name || "Opponent 2"}</span>
                                                </div>
                                            </div>

                                            {/* Score Section */}
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                                                <div style={{
                                                    fontSize: '1.7rem',
                                                    fontWeight: '900',
                                                    color: isWin ? THEME_COLOR : '#fff',
                                                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                                                    background: 'rgba(0, 0, 0, 0.4)',
                                                    padding: '6px 16px',
                                                    borderRadius: '8px',
                                                    border: isWin ? `1px solid ${THEME_COLOR}33` : '1px solid rgba(255,255,255,0.05)',
                                                    boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.3)',
                                                    letterSpacing: '1px'
                                                }}>
                                                    {userScore}-{oppScore}
                                                </div>

                                                <div style={{
                                                    width: '85px',
                                                    textAlign: 'center',
                                                    fontSize: '0.7rem',
                                                    fontWeight: '900',
                                                    letterSpacing: '1.5px',
                                                    color: isWin ? THEME_COLOR : 'rgba(255,255,255,0.4)',
                                                    background: isWin ? `${THEME_COLOR}1f` : 'rgba(255,255,255,0.05)',
                                                    textTransform: 'uppercase',
                                                    padding: '6px 0',
                                                    border: `1px solid ${isWin ? `${THEME_COLOR}4d` : 'rgba(255,255,255,0.1)'}`,
                                                    borderRadius: '6px'
                                                }}>
                                                    {isWin ? 'WIN' : 'DINKED'}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* CHOICE 4: THE VIBE (Ultra-Premium Watermark) */}
                    {layoutMode === 'VIBE' && (
                        <div style={{
                            flex: 1,
                            width: '100%',
                            height: '100%',
                            // Changed to semi-transparent black for the sticker look
                            backgroundColor: isTransparent ? 'rgba(0, 0, 0, 0.7)' : '#000',
                            backdropFilter: 'blur(20px)',
                            WebkitBackdropFilter: 'blur(20px)',
                            position: 'relative',
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'hidden',
                            fontFamily: '"Syncopate", sans-serif',
                            borderRadius: '32px', // Added radius to make it feel like a sticker
                            border: '1px solid rgba(255, 255, 255, 0.1)'
                        }}>
                            {/* 1. VERTICAL SIDEBAR (Session Info) */}
                            <div style={{
                                position: 'absolute',
                                left: '20px',
                                top: '20px',
                                bottom: '20px',
                                width: '40px',
                                borderLeft: `1px solid ${THEME_COLOR}`,
                                zIndex: 5,
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'flex-end',
                                paddingLeft: '10px'
                            }}>
                                <div style={{
                                    transform: 'rotate(-90deg) translateX(0)',
                                    transformOrigin: 'left bottom',
                                    whiteSpace: 'nowrap',
                                    color: THEME_COLOR,
                                    fontSize: '0.7rem',
                                    fontWeight: '900',
                                    letterSpacing: '5px',
                                    textTransform: 'uppercase'
                                }}>
                                    {session.name || 'RRC OFFICIAL'} — {new Date().toLocaleDateString()}
                                </div>
                            </div>

                            {/* 2. MAIN CONTENT AREA */}
                            <div style={{
                                marginTop: 'auto',
                                padding: '60px 40px',
                                zIndex: 10,
                                position: 'relative'
                            }}>
                                {/* BIG NAME BACKGROUND TEXT */}
                                <div style={{
                                    position: 'absolute',
                                    top: '-40px',
                                    left: '40px',
                                    fontSize: '8rem',
                                    fontWeight: '900',
                                    color: 'transparent',
                                    WebkitTextStroke: '1px rgba(255,255,255,0.1)',
                                    zIndex: -1,
                                    lineHeight: 1
                                }}>
                                    {user.displayName.split(' ')[0].toUpperCase()}
                                </div>

                                <div style={{ display: 'flex', alignItems: 'flex-end', gap: '20px' }}>
                                    <div style={{ flex: 1 }}>
                                        <div style={{
                                            backgroundColor: THEME_COLOR,
                                            color: '#ffffff',
                                            display: 'inline-block',
                                            padding: '4px 12px',
                                            fontSize: '0.8rem',
                                            fontWeight: '900',
                                            marginBottom: '10px'
                                        }}>
                                            {LEVELS_MAP[user?.level] || 'PLAYER'}
                                        </div>
                                        <h1 style={{
                                            color: '#fff',
                                            fontSize: '4.5rem',
                                            margin: 0,
                                            lineHeight: 0.9,
                                            fontWeight: '900',
                                            letterSpacing: '-2px'
                                        }}>
                                            {user.displayName.toUpperCase()}
                                        </h1>
                                        <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '1.2rem', marginTop: '5px' }}>
                                            @{user.username || 'juandelacruz'}
                                        </div>
                                    </div>

                                    {/* LOGO */}
                                    <img src={CLUB_LOGO_URL} style={{ width: '180px', height: '180px', objectFit: 'contain' }} />
                                </div>

                                {/* STATS STRIP */}
                                <div style={{
                                    display: 'flex',
                                    marginTop: '40px',
                                    gap: '40px',
                                    borderTop: '1px solid rgba(255,255,255,0.2)',
                                    paddingTop: '20px'
                                }}>
                                    <div>
                                        <div style={{ color: THEME_COLOR, fontSize: '0.7rem', fontWeight: '900' }}>WINS</div>
                                        <div style={{ color: '#fff', fontSize: '2.5rem', fontWeight: '900' }}>{stats.wins}</div>
                                    </div>
                                    <div>
                                        <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.7rem', fontWeight: '900' }}>LOSSES</div>
                                        <div style={{ color: '#fff', fontSize: '2.5rem', fontWeight: '900' }}>{stats.losses}</div>
                                    </div>
                                    <div>
                                        <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.7rem', fontWeight: '900' }}>RATIO</div>
                                        <div style={{ color: '#fff', fontSize: '2.5rem', fontWeight: '900' }}>
                                            {((stats.wins / (stats.wins + stats.losses || 1)) * 100).toFixed(0)}%
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* 3. DECORATIVE CORNER ELEMENT */}
                            <div style={{
                                position: 'absolute',
                                top: '40px',
                                right: '40px',
                                textAlign: 'right',
                                zIndex: 10
                            }}>
                                <div style={{ color: '#fff', fontSize: '0.6rem', letterSpacing: '3px', opacity: 0.6 }}>VERIFIED PLAYER</div>
                                <div style={{ width: '40px', height: '2px', background: THEME_COLOR, marginLeft: 'auto', marginTop: '5px' }} />
                            </div>
                        </div>
                    )}


                    {/* --- ENHANCED PREMIUM BRAND FOOTER --- */}
                    {showFooter && (
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '12px',
                            width: '100%',
                            marginTop: '50px',
                            padding: '10px 0',
                            position: 'relative',
                            zIndex: 100
                        }}>
                            {/* BACKGROUND BRANDING EMBLEM IMAGE */}
                            <img
                                src="/img/pickle.png"
                                alt="Pickleball Background Accent"
                                style={{
                                    position: 'absolute',
                                    top: layoutMode === 'VIBE' ? '-280%' : '-280%',
                                    left: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    width: '450px',
                                    height: '450px',
                                    objectFit: 'contain',
                                    opacity:layoutMode === 'VIBE' ? 0.3 : 0.5,
                                    pointerEvents: 'none',
                                    userSelect: 'none',
                                    zIndex: 0
                                }}
                            />
                            {/* 1. CLEAN DIVIDER: No glow, just high-contrast visibility */}
                            {/* <div style={{
        height: '1px',
        width: '240px',
        background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
        position: 'relative'
    }}>
        <div style={{
            position: 'absolute',
            top: '-1px', left: '50%', transform: 'translateX(-50%)',
            width: '40px', height: '3px', 
            backgroundColor: THEME_COLOR,
            borderRadius: '2px'
        }} />
    </div> */}

                            {/* 2. THE CLEAN PILL: Sharpened borders, no bleeding shadows */}
                            <div style={{
                                backgroundColor: THEME_COLOR, // Slightly more solid to stop bleed
                                backdropFilter: 'blur(15px)',
                                WebkitBackdropFilter: 'blur(15px)',

                                padding: '10px 28px',
                                borderRadius: '50px', // Perfect rounded pill
                                display: 'flex',
                                alignItems: 'center',
                                gap: '16px',

                                // SHARP BORDER: Inner light stroke + Thin dark outer ring for definition
                                border: '1px solid rgba(255, 255, 255, 0.2)',
                                boxShadow: '0 4px 15px rgba(0,0,0,0.4)'
                            }}>
                                {/* ICON: Cleaned up shadow */}
                                {/* <div style={{
                                fontSize: '1.8rem',
                                display: 'flex',
                                alignItems: 'center'
                            }}>
                                🦝
                            </div> */}

                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                    <div style={{
                                        color: '#FFFFFF',
                                        fontSize: '1.3rem',
                                        fontWeight: '900',
                                        letterSpacing: '5px',
                                        textTransform: 'uppercase',
                                        lineHeight: '1.1'
                                    }}>
                                        {CLUB_NAME}
                                    </div>

                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        marginTop: '2px'
                                    }}>
                                        <div style={{ height: '1px', flex: 1, background: 'rgba(255,255,255,0.15)' }} />
                                        <div style={{
                                            color: 'rgba(255,255,255,0.5)',
                                            fontSize: '0.6rem',
                                            fontWeight: '700',
                                            letterSpacing: '4px',
                                            textTransform: 'uppercase'
                                        }}>
                                            EST. 2026
                                        </div>
                                        <div style={{ height: '1px', flex: 1, background: 'rgba(255,255,255,0.15)' }} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            {/* FULL SCREEN OVERLAY FOR GENERATED IMAGE */}
            {showFullPreview && (
                <div
                    className="preview-overlay"
                    style={{
                        position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
                        backgroundColor: 'rgba(0,0,0,0.9)', zIndex: 9999,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)'
                    }}
                    onClick={() => setShowFullPreview(false)}
                >
                    <div className="position-relative text-center p-3">
                        {/* Close Button */}
                        <button
                            className="btn btn-light rounded-circle position-absolute top-0 end-0 m-2 shadow"
                            onClick={() => setShowFullPreview(false)}
                            style={{ zIndex: 10001, width: '40px', height: '40px' }}
                        >
                            <i className="bi bi-x-lg"></i>
                        </button>

                        <img
                            src={previewUrl}
                            className="img-fluid rounded shadow-lg"
                            style={{ maxHeight: '85vh', maxWidth: '95vw', border: '2px solid rgba(255,255,255,0.2)' }}
                            onClick={(e) => e.stopPropagation()} // Prevents closing when clicking the image itself
                        />

                        <div className="mt-3 text-white-50 small">
                            Long press to save or use the share buttons below
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}